<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit            from '@tiptap/starter-kit'
import Image                 from '@tiptap/extension-image'
import Placeholder           from '@tiptap/extension-placeholder'

const props = defineProps({
    modelValue  : { type: String,  default: '' },
    editable    : { type: Boolean, default: true },
    placeholder : { type: String,  default: 'Your text here...' },
    enableLinks : { type: Boolean, default: false },
    enableImages: { type: Boolean, default: false },
    height      : { type: String,  default: '200px' }
})
const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'update'])

const editor = ref(null)
const hasFocus = ref(false)
const lastSelection = ref(null)

const initEditor = () => {
    editor.value = new Editor({
        content     : props.modelValue,
        editable    : props.editable,
        extensions  : [
            StarterKit.configure({
                history: { depth: 100, newGroupDelay: 300 },
                underline: {},
                link: {
                    openOnClick   : false,
                    autolink      : true,
                    protocols     : ['http', 'https', 'mailto', 'tel'],
                    validate      : href => /^((https?:)?\/\/|mailto:|tel:).+/.test(href)
                }
            }),
            Image.configure({
                HTMLAttributes: { class: 'max-w-full h-auto rounded' }
            }),
            Placeholder.configure({
                placeholder   : props.placeholder
            })
        ],
        onUpdate   : ({ editor }) => {
            emit('update:modelValue', editor.getHTML())
            emit('update', editor.getHTML())
        },
        onSelectionUpdate: ({ editor }) => {
            lastSelection.value = {
                from: editor.state.selection.from,
                to: editor.state.selection.to,
            }
        },
        onFocus    : () => {
            hasFocus.value = true
            emit('focus')
        },
        onBlur     : () => {
            hasFocus.value = false
            lastSelection.value = editor.value
                ? {
                    from: editor.value.state.selection.from,
                    to: editor.value.state.selection.to,
                }
                : null
            emit('blur')
        }
    })
}

onMounted(initEditor)
onBeforeUnmount(() => editor.value?.destroy())

// Mantieni in sync contenuto esterno → editor (evita loop)
watch(
    () => props.modelValue,
    (html) => {
        const current = editor.value?.getHTML()
        if (editor.value && html !== current) {
            editor.value.commands.setContent(html || '', false)
        }
    }
)

// Abilita/disabilita editing runtime
watch(
    () => props.editable,
    (val) => editor.value?.setEditable(val)
)

// -------- Toolbar helpers --------
const canUndo = computed(() => editor.value?.can().undo() ?? false)
const canRedo = computed(() => editor.value?.can().redo() ?? false)

const setHeading = (level) => {
    editor.value?.chain().focus().toggleHeading({ level }).run()
}

const setLink = async () => {
    const prev = editor.value?.getAttributes('link')?.href ?? ''
    const url  = window.prompt('Inserisci URL', prev)
    if (url === null) return
    if (url === '') {
        editor.value?.chain().focus().unsetLink().run()
        return
    }
    editor.value?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

const addImageByUrl = async () => {
    const url = window.prompt('URL immagine')
    if (!url) return
    editor.value?.chain().focus().setImage({ src: url }).run()
}

const clearFormatting = () => {
    editor.value?.chain().focus().unsetAllMarks().clearNodes().run()
}

const insertContent = (content) => {
    if (!editor.value || !content) {
        return
    }

    if (hasFocus.value) {
        editor.value.chain().focus().insertContent(content).run()
        return
    }

    if (lastSelection.value) {
        editor.value.chain().insertContentAt(lastSelection.value, content).run()
        return
    }

    editor.value.chain().focus('end').insertContent(content).run()
}

defineExpose({
    insertContent,
    focusEnd: () => editor.value?.chain().focus('end').run(),
})
</script>

<template>
    <div class="w-full">
        <!-- Toolbar -->
        <div class="flex flex-wrap gap-2 rounded-xl border bg-white p-2 shadow-sm">
            <!-- Text Formatting -->
            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('bold') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleBold().run()"
                title="Bold"
            >
                <fa-icon icon="bold" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('italic') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleItalic().run()"
                title="Italic"
            >
                <fa-icon icon="italic" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('underline') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleUnderline().run()"
                title="Underline"
            >
                <fa-icon icon="underline" />
            </button>

            <div class="w-px self-stretch bg-gray-200 mx-1" />

            <!-- Headings -->
            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('heading', { level: 1 }) ? 'bg-gray-100' : ''"
                @click="setHeading(1)"
                title="Heading 1"
            >
                <fa-icon icon="heading" class="mr-1" />
                <span class="text-xs">1</span>
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('heading', { level: 2 }) ? 'bg-gray-100' : ''"
                @click="setHeading(2)"
                title="Heading 2"
            >
                <fa-icon icon="heading" class="mr-1" />
                <span class="text-xs">2</span>
            </button>

            <div class="w-px self-stretch bg-gray-200 mx-1" />

            <!-- Lists -->
            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('bulletList') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleBulletList().run()"
                title="Bullet List"
            >
                <fa-icon icon="list-ul" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('orderedList') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleOrderedList().run()"
                title="Numbered List"
            >
                <fa-icon icon="list-ol" />
            </button>

            <!-- Link & Image (conditional) -->
            <template v-if="enableLinks || enableImages">
                <div class="w-px self-stretch bg-gray-200 mx-1" />

                <button
                    type="button"
                    v-if="enableLinks"
                    class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                    :class="editor?.isActive('link') ? 'bg-gray-100' : ''"
                    @click="setLink"
                    title="Insert Link"
                >
                    <fa-icon icon="link" />
                </button>

                <button
                    type="button"
                    v-if="enableImages"
                    class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                    @click="addImageByUrl"
                    title="Insert Image"
                >
                    <fa-icon icon="image" />
                </button>
            </template>

            <div class="w-px self-stretch bg-gray-200 mx-1" />

            <!-- History & Clear -->
            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                :disabled="!canUndo"
                @click="editor?.chain().focus().undo().run()"
                title="Undo"
            >
                <fa-icon icon="rotate-left" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                :disabled="!canRedo"
                @click="editor?.chain().focus().redo().run()"
                title="Redo"
            >
                <fa-icon icon="rotate-right" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                @click="clearFormatting"
                title="Clear Formatting"
            >
                <fa-icon icon="eraser" />
            </button>
        </div>

        <!-- Editor -->
        <div
            class="cm-input-borders mt-2 bg-white p-3 prose max-w-none overflow-y-auto"
            :style="{ height: height }"
        >
            <EditorContent :editor="editor" />
        </div>
    </div>
</template>

<style scoped>
.prose :deep(p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    float: left;
    height: 0;
    color: #9ca3af; /* gray-400 */
    pointer-events: none;
}

/* Rimuovi outline blu al focus */
.prose :deep(.ProseMirror) {
    outline: none;
}

.prose :deep(.ProseMirror:focus) {
    outline: none;
}
</style>
